import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ProjectsTable from './ProjectsTable';
import CustomFieldFilter from './CustomFieldFilter';
import { Button, SearchBar, Dropdown, Popover, FilterChip, Checkbox } from './ui';
import { Filter, Settings, GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { ProjectService } from '../services/projectService';
import { DiscrepancyService } from '../services/discrepancyService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Column Item Component
const SortableColumnItem = ({ column, checked, onChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
        backgroundColor: isDragging ? 'var(--dark-elevated)' : 'transparent',
        borderRadius: 'var(--radius-md)',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'var(--gray-400)',
          padding: '4px',
        }}
      >
        <GripVertical size={16} />
      </div>
      <Checkbox
        label={column.label}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
};

const PAGE_SIZE = 50;

const CompletedProjectsTable = ({
  projects: initialProjects = [], // Will be ignored - we fetch our own
  activeTab,
  tabConfig,
  onUpdateProject,
  onRowClick,
  loading: parentLoading,
  updatingProjects,
  clients = [],
  employees = [],
  filteredEmployees = [],
  sortBy,
  sortDirection,
  onSort,
  discrepanciesByProject: parentDiscrepancies,
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [paginatedProjects, setPaginatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discrepanciesByProject, setDiscrepanciesByProject] = useState({});

  // Search state - for searching ALL projects
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = not searching, array = search results
  const [searchLoading, setSearchLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [filters, setFilters] = useState({
    client: '',
  });
  const [customFilters, setCustomFilters] = useState([]);
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(() => {
    const saved = localStorage.getItem('completed-auto-resize');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Column visibility - default all visible (except status)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('completed-visible-columns');
    return saved ? JSON.parse(saved) : {
      jobNumber: true,
      project: true,
      client: true,
      readyFor: true,
      dueDate: true,
      finalProjectedCompletionDate: true,
      float: true,
      modelBuilder: true,
      contact: true,
      readyToInvoice: true,
      discrepancies: true,
    };
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('completed-column-order');
    return saved ? JSON.parse(saved) : [];
  });

  // Fetch completed projects with pagination
  const fetchCompletedProjects = useCallback(async (page) => {
    setLoading(true);
    try {
      const result = await ProjectService.getCompletedProjectsPaginated({
        page,
        limit: PAGE_SIZE,
        searchTerm: '', // Don't filter by search - that's handled separately
      });

      setPaginatedProjects(result.projects);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setCurrentPage(page);

      // Fetch discrepancies for these projects
      if (result.projects.length > 0) {
        const projectIds = result.projects.map(p => p.id);
        const discrepancies = await DiscrepancyService.getDiscrepanciesByProjectIds(projectIds);
        setDiscrepanciesByProject(discrepancies);
      }
    } catch (error) {
      console.error('Error fetching completed projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchCompletedProjects(1);
  }, [fetchCompletedProjects]);

  // Debounced search for ALL projects
  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setSearchResults(null);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await ProjectService.searchAllProjectsForCompleted(searchTerm, 100);
        setSearchResults(result.projects);

        // Fetch discrepancies for search results
        if (result.projects.length > 0) {
          const projectIds = result.projects.map(p => p.id);
          const discrepancies = await DiscrepancyService.getDiscrepanciesByProjectIds(projectIds);
          setDiscrepanciesByProject(discrepancies);
        }
      } catch (error) {
        console.error('Error searching projects:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Determine which projects to display
  const displayProjects = searchResults !== null ? searchResults : paginatedProjects;
  const isSearching = searchResults !== null;

  // Get unique clients for filter dropdown (from displayed projects)
  const clientOptions = useMemo(() => {
    const uniqueClients = [...new Set(displayProjects.map(p => p.clientName).filter(Boolean))];
    const sortedClients = uniqueClients.sort();

    return [
      { value: '', label: 'All Clients' },
      ...sortedClients.map(name => ({ value: name, label: name }))
    ];
  }, [displayProjects]);

  // Helper function to get nested field value
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  };

  // Helper function to apply custom filter logic
  const applyCustomFilter = useCallback((project, filter) => {
    const fieldValue = getNestedValue(project, filter.field);
    const filterValue = filter.value?.toLowerCase() || '';

    // Handle array fields
    if (Array.isArray(fieldValue)) {
      const arrayString = fieldValue.join(', ').toLowerCase();
      switch (filter.condition) {
        case 'equals':
          return arrayString === filterValue;
        case 'contains':
          return arrayString.includes(filterValue);
        case 'starts_with':
          return arrayString.startsWith(filterValue);
        case 'ends_with':
          return arrayString.endsWith(filterValue);
        case 'is_empty':
          return fieldValue.length === 0;
        case 'is_not_empty':
          return fieldValue.length > 0;
        case 'not_equals':
          return arrayString !== filterValue;
        case 'not_contains':
          return !arrayString.includes(filterValue);
        default:
          return true;
      }
    }

    // Handle string fields
    const stringValue = String(fieldValue || '').toLowerCase();
    switch (filter.condition) {
      case 'equals':
        return stringValue === filterValue;
      case 'contains':
        return stringValue.includes(filterValue);
      case 'starts_with':
        return stringValue.startsWith(filterValue);
      case 'ends_with':
        return stringValue.endsWith(filterValue);
      case 'is_empty':
        return !fieldValue;
      case 'is_not_empty':
        return !!fieldValue;
      case 'not_equals':
        return stringValue !== filterValue;
      case 'not_contains':
        return !stringValue.includes(filterValue);
      default:
        return true;
    }
  }, []);

  // Filter projects based on client filter and custom filters (applied client-side after fetch)
  const filteredProjects = useMemo(() => {
    let filtered = displayProjects;

    // Apply client filter
    if (filters.client) {
      filtered = filtered.filter(p => p.clientName === filters.client);
    }

    // Apply custom filters
    if (customFilters.length > 0) {
      filtered = filtered.filter(project =>
        customFilters.every(filter => applyCustomFilter(project, filter))
      );
    }

    return filtered;
  }, [displayProjects, filters, customFilters, applyCustomFilter]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);

      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      setColumnOrder(newOrder);
      localStorage.setItem('completed-column-order', JSON.stringify(newOrder));
    }
  };

  // Define available columns
  const availableColumns = useMemo(() => [
    { id: 'jobNumber', label: 'Job Number' },
    { id: 'project', label: 'Project' },
    { id: 'client', label: 'Client' },
    { id: 'readyFor', label: 'Ready-For' },
    { id: 'dueDate', label: 'Due Date' },
    { id: 'finalProjectedCompletionDate', label: 'Projected Completion' },
    { id: 'float', label: 'Float' },
    { id: 'modelBuilder', label: 'Model Builder' },
    { id: 'contact', label: 'Contact' },
    { id: 'readyToInvoice', label: 'Ready to Invoice' },
    { id: 'discrepancies', label: 'Discrepancies' },
  ], []);

  // Initialize column order if empty
  useEffect(() => {
    if (columnOrder.length === 0 && availableColumns.length > 0) {
      const defaultOrder = availableColumns.map(col => col.id);
      setColumnOrder(defaultOrder);
      localStorage.setItem('completed-column-order', JSON.stringify(defaultOrder));
    }
  }, [availableColumns, columnOrder.length]);

  const handleColumnVisibilityChange = (columnId, checked) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnId]: checked
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('completed-visible-columns', JSON.stringify(newVisibleColumns));
  };

  // Handle row click - open project details instead of subitems
  const handleRowClick = (project, type) => {
    // For completed tab, always open project details (info)
    onRowClick(project, 'info');
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchCompletedProjects(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchCompletedProjects(currentPage + 1);
    }
  };

  const handlePageChange = (page) => {
    fetchCompletedProjects(page);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of visible range
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if at the start
      if (currentPage <= 3) {
        end = 4;
      }
      // Adjust if at the end
      if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      // Add ellipsis if needed
      if (start > 2) {
        pages.push('...');
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (end < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-4)',
        backgroundColor: 'var(--dark-bg)',
        borderBottom: '1px solid var(--gray-600)',
        flexWrap: 'wrap',
        gap: 'var(--space-3)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flex: 1 }}>
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search all projects..."
              size="sm"
            />
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowFilters(!showFilters);
                    setShowColumnSettings(false);
                  }}
                >
                  <Filter size={16} />
                  Filters
                </Button>
              }
              position="bottom-left"
              width="400px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <h3 style={{
                  margin: 0,
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)'
                }}>
                  Filter Projects
                </h3>

                {/* Client Filter */}
                <Dropdown
                  label="Client"
                  value={filters.client}
                  options={clientOptions}
                  onChange={(value) => setFilters({ ...filters, client: value })}
                  placeholder="Select client"
                  searchable={true}
                  size="md"
                />

                {/* Divider */}
                <div style={{ borderTop: '1px solid var(--gray-700)' }} />

                {/* Advanced Filters */}
                <div>
                  <h4 style={{
                    margin: 0,
                    marginBottom: 'var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-300)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Advanced Filters
                  </h4>
                  <CustomFieldFilter
                    currentFilters={customFilters}
                    onFiltersChange={setCustomFilters}
                    fieldOptions={[
                      { key: "projectName", label: "Project Name", type: "string" },
                      { key: "jobNumber", label: "Job Number", type: "string" },
                      { key: "clientName", label: "Client", type: "string" },
                      { key: "contactName", label: "Contact", type: "string" },
                      { key: "modelBuilderEmployee.name", label: "Model Builder", type: "string" },
                      { key: "readyForEmployee.name", label: "Ready-For", type: "string" },
                      { key: "dueDate", label: "Due Date", type: "string" },
                    ]}
                  />
                </div>

                {/* Footer Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: 'var(--space-3)',
                  borderTop: '1px solid var(--gray-700)'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFilters({ client: '' });
                      setCustomFilters([]);
                    }}
                  >
                    Clear all
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </Popover>

          <Popover
            isOpen={showColumnSettings}
            onClose={() => setShowColumnSettings(false)}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowColumnSettings(!showColumnSettings);
                  setShowFilters(false);
                }}
              >
                <Settings size={16} />
                Columns
              </Button>
            }
            position="bottom-left"
            width="520px"
            maxWidth="90vw"
          >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Header with count */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)'
                  }}>
                    Toggle Columns
                  </h3>
                  <span style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--gray-400)',
                    backgroundColor: 'var(--dark-elevated)',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    fontWeight: 'var(--font-medium)'
                  }}>
                    {Object.values(visibleColumns).filter(Boolean).length} / {Object.keys(visibleColumns).length} visible
                  </span>
                </div>

                {/* Quick Actions */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  paddingBottom: 'var(--space-3)',
                  borderBottom: '1px solid var(--gray-700)',
                  flexWrap: 'wrap'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allVisible = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allVisible[key] = true;
                      });
                      setVisibleColumns(allVisible);
                      localStorage.setItem('completed-visible-columns', JSON.stringify(allVisible));
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allHidden = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allHidden[key] = false;
                      });
                      setVisibleColumns(allHidden);
                      localStorage.setItem('completed-visible-columns', JSON.stringify(allHidden));
                    }}
                  >
                    Deselect All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const defaultOrder = availableColumns.map(col => col.id);
                      setColumnOrder(defaultOrder);
                      localStorage.setItem('completed-column-order', JSON.stringify(defaultOrder));
                    }}
                  >
                    Reset Order
                  </Button>
                </div>

                {/* Column Checkboxes with Drag & Drop */}
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: 'var(--space-2)'
                }}>
                  {/* Visible Columns (Checked) */}
                  <div style={{ marginBottom: 'var(--space-3)' }}>
                    <div style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-400)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 'var(--space-2)',
                      paddingLeft: 'var(--space-2)'
                    }}>
                      Visible Columns
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={columnOrder.filter(id => visibleColumns[id])}
                        strategy={verticalListSortingStrategy}
                      >
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-1)'
                        }}>
                          {columnOrder
                            .filter(id => visibleColumns[id])
                            .map(colId => {
                              const col = availableColumns.find(c => c.id === colId);
                              if (!col) return null;
                              return (
                                <SortableColumnItem
                                  key={col.id}
                                  column={col}
                                  checked={visibleColumns[col.id]}
                                  onChange={(e) => handleColumnVisibilityChange(col.id, e.target.checked)}
                                />
                              );
                            })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>

                  {/* Hidden Columns (Unchecked) */}
                  {columnOrder.filter(id => !visibleColumns[id]).length > 0 && (
                    <div style={{
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <div style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-400)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 'var(--space-2)',
                        paddingLeft: 'var(--space-2)'
                      }}>
                        Hidden Columns
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={columnOrder.filter(id => !visibleColumns[id])}
                          strategy={verticalListSortingStrategy}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-1)'
                          }}>
                            {columnOrder
                              .filter(id => !visibleColumns[id])
                              .map(colId => {
                                const col = availableColumns.find(c => c.id === colId);
                                if (!col) return null;
                                return (
                                  <SortableColumnItem
                                    key={col.id}
                                    column={col}
                                    checked={visibleColumns[col.id]}
                                    onChange={(e) => handleColumnVisibilityChange(col.id, e.target.checked)}
                                  />
                                );
                              })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                </div>
              </div>
            </Popover>

            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => {
                const newValue = !autoResizeEnabled;
                setAutoResizeEnabled(newValue);
                localStorage.setItem('completed-auto-resize', JSON.stringify(newValue));
              }}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Results count and pagination info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          fontSize: 'var(--text-sm)',
          color: 'var(--gray-400)',
        }}>
          {isSearching ? (
            <span>
              {searchLoading ? 'Searching...' : `Found ${filteredProjects.length} project${filteredProjects.length !== 1 ? 's' : ''} across all statuses`}
            </span>
          ) : (
            <span>
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} completed projects
            </span>
          )}
        </div>
      </div>

      {/* Search indicator */}
      {isSearching && (
        <div style={{
          padding: 'var(--space-3) var(--space-4)',
          backgroundColor: 'var(--status-info-bg)',
          borderBottom: '1px solid var(--gray-600)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
        }}>
          <span style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--interactive-primary)',
            fontWeight: 'var(--font-medium)',
          }}>
            Searching across all project statuses for: "{searchTerm}"
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchTerm('')}
          >
            Clear search
          </Button>
        </div>
      )}

      {/* Active Filter Chips */}
      {(filters.client || customFilters.length > 0) && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          padding: 'var(--space-3) var(--space-4)',
          backgroundColor: 'var(--dark-bg)',
          borderBottom: '1px solid var(--gray-600)',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--gray-400)',
            fontWeight: 'var(--font-medium)'
          }}>
            Active filters:
          </span>
          {filters.client && (
            <FilterChip
              label={`Client: ${filters.client}`}
              onRemove={() => setFilters({ ...filters, client: '' })}
            />
          )}
          {customFilters.map((filter, index) => {
            const fieldOptions = [
              { key: "projectName", label: "Project Name" },
              { key: "jobNumber", label: "Job Number" },
              { key: "clientName", label: "Client" },
              { key: "contactName", label: "Contact" },
              { key: "modelBuilderEmployee.name", label: "Model Builder" },
              { key: "readyForEmployee.name", label: "Ready-For" },
              { key: "dueDate", label: "Due Date" },
            ];
            const fieldLabel = fieldOptions.find(f => f.key === filter.field)?.label || filter.field;
            return (
              <FilterChip
                key={filter.id}
                label={`${fieldLabel} ${filter.condition} ${filter.value || ''}`}
                onRemove={() => {
                  const newFilters = customFilters.filter(f => f.id !== filter.id);
                  setCustomFilters(newFilters);
                }}
              />
            );
          })}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilters({ client: '' });
              setCustomFilters([]);
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Projects Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <ProjectsTable
          projects={filteredProjects}
          activeTab={activeTab}
          tabConfig={tabConfig}
          onUpdateProject={onUpdateProject}
          onRowClick={handleRowClick}
          loading={loading || searchLoading}
          updatingProjects={updatingProjects}
          clients={clients}
          employees={employees}
          filteredEmployees={filteredEmployees}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={onSort}
          discrepanciesByProject={discrepanciesByProject}
          autoResizeEnabled={autoResizeEnabled}
          hiddenColumns={['status']}
        />
      </div>

      {/* Pagination Controls - only show when not searching */}
      {!isSearching && totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-4)',
          backgroundColor: 'var(--dark-elevated)',
          borderTop: '1px solid var(--gray-600)',
        }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || loading}
            style={{ padding: '8px' }}
          >
            <ChevronLeft size={18} />
          </Button>

          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span
                key={`ellipsis-${index}`}
                style={{
                  padding: '0 var(--space-2)',
                  color: 'var(--gray-500)',
                }}
              >
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant={currentPage === page ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => handlePageChange(page)}
                disabled={loading}
                style={{
                  minWidth: '36px',
                  padding: '8px 12px',
                }}
              >
                {page}
              </Button>
            )
          ))}

          <Button
            variant="ghost"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || loading}
            style={{ padding: '8px' }}
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompletedProjectsTable;
